import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Button,
    Grid,
    Divider,
    Spinner
} from '@chakra-ui/react';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

interface Plan {
    number_of_days: number;
    amount: number;
    amount_off: number;
    plan_level: number;
    plan_name: string;
}

const stripePromise = process.env.REACT_APP_STRIPE_PUBLIC_KEY
    ? loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY)
    : Promise.resolve(null);

const CheckoutForm = ({ ...props }) => {

    const [cookies, , removeCookie] = useCookies(['Authorization', 'firstName', 'lastName']);
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const queryParams = new URLSearchParams(location.search);
    const plan_level = queryParams.get('plan_level') || '';

    const [loading, setLoading] = useState(true);
    const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [checkoutError, setCheckoutError] = useState('');

    const checkoutOptions = useMemo(() => (
        clientSecret ? { clientSecret } : null
    ), [clientSecret]);

    const getAllPLans = async () => {
        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': cookies.Authorization
            },
        }
        const response = await fetch(process.env.REACT_APP_BASE_URL + "get_all_plans", requestOptions)
        const data = await response.json()
        if (data.success) {
            const plan = data.plans.find((p: Plan) => p.plan_level === parseInt(plan_level));
            setSelectedPlan(plan || null);
        }
        setLoading(false);
    }

    const handleSubmit = async () => {
        if (!selectedPlan || checkoutLoading) return;

        setCheckoutError('');
        setCheckoutLoading(true);

        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': cookies.Authorization
            },
            body: JSON.stringify({
                plan_level: selectedPlan.plan_level,
                payment_method: 'credit_card',
            })
        }

        try {
            const response = await fetch(process.env.REACT_APP_BASE_URL + "create_checkout_session", requestOptions)
            const data = await response.json()

            if (response.status === 401) {
                removeCookie('Authorization', { path: '/' });
                removeCookie('firstName', { path: '/' });
                removeCookie('lastName', { path: '/' });
                navigate(`/Login?originalURL=${encodeURIComponent(location.pathname + location.search)}`);
                return;
            }

            if (response.ok && data.success && data.clientSecret) {
                setClientSecret(data.clientSecret);
            } else {
                setCheckoutError(data.error || t('checkout.create_order_failed'));
            }
        } catch (error) {
            setCheckoutError(t('checkout.network_error'));
        } finally {
            setCheckoutLoading(false);
        }
    };

    useEffect(() => {
        getAllPLans();
    }, []);

    if (loading) {
        return (
            <Box minH="calc(100vh - 64px)" bg="canvas" display="flex" alignItems="center" justifyContent="center">
                <Spinner size="xl" color="brand.primary" />
            </Box>
        );
    }

    return (
        <Box
            minH="calc(100vh - 64px)"
            bg="canvas"
            py={{ base: 8, md: 12 }}
        >
            <Container maxW="900px" px={{ base: 4, md: 8 }}>
                <VStack spacing={8} align="stretch">
                    <Heading
                        size="xl"
                        color="ink"
                        letterSpacing={'-0.5px'}
                    >
                        {t('checkout.checkout')}
                    </Heading>

                    <Grid templateColumns={{ base: "1fr", md: "1fr 360px" }} gap={8}>
                        <Box
                            p={{ base: 6, md: 8 }}
                            borderRadius="xl"
                            bg="surfaceCard"
                            border="1px solid"
                            borderColor="hairline"
                        >
                            {clientSecret && checkoutOptions ? (
                                <EmbeddedCheckoutProvider
                                    key={clientSecret}
                                    stripe={stripePromise}
                                    options={checkoutOptions}
                                >
                                    <EmbeddedCheckout />
                                </EmbeddedCheckoutProvider>
                            ) : (
                                <VStack spacing={6} align="stretch">
                                    <Heading size="md" color="ink">
                                        {t('checkout.payment_details')}
                                    </Heading>

                                    <Text fontSize="sm" color="bodyStrong">
                                        {t('checkout.credit_card')}
                                    </Text>

                                    <Divider borderColor="hairline" />

                                    {checkoutError && (
                                        <Text fontSize="sm" color="error">
                                            {checkoutError}
                                        </Text>
                                    )}

                                    <Button
                                        variant="primary"
                                        size="lg"
                                        w="100%"
                                        mt={2}
                                        onClick={handleSubmit}
                                        isDisabled={!selectedPlan}
                                        isLoading={checkoutLoading}
                                        loadingText={t('common.loading')}
                                    >
                                        {t('checkout.pay_now')}
                                    </Button>
                                </VStack>
                            )}
                        </Box>

                        <Box
                            p={{ base: 6, md: 6 }}
                            borderRadius="xl"
                            bg="surfaceSoft"
                            border="1px solid"
                            borderColor="hairline"
                            h="fit-content"
                        >
                            <VStack spacing={5} align="stretch">
                                <Heading size="md" color="ink">
                                    {t('checkout.order_summary')}
                                </Heading>

                                {selectedPlan && (
                                    <>
                                        <HStack justify="space-between">
                                            <Text fontSize="sm" color="bodyStrong">
                                                {t('checkout.days_plan', { days: selectedPlan.number_of_days })}
                                            </Text>
                                            <Text fontSize="sm" fontWeight={500} color="ink">
                                                ${selectedPlan.amount.toFixed(2)}
                                            </Text>
                                        </HStack>

                                        {selectedPlan.amount_off > 0 && (
                                            <HStack justify="space-between">
                                                <Text fontSize="sm" color="success">
                                                    {t('checkout.discount')}
                                                </Text>
                                                <Text fontSize="sm" fontWeight={500} color="success">
                                                    -${selectedPlan.amount_off.toFixed(2)}
                                                </Text>
                                            </HStack>
                                        )}

                                        <Divider borderColor="hairline" />

                                        <HStack justify="space-between">
                                            <Text fontWeight={600} color="ink">
                                                {t('checkout.total')}
                                            </Text>
                                            <Text fontWeight={600} fontSize="lg" color="brand.primary">
                                                ${(selectedPlan.amount - selectedPlan.amount_off).toFixed(2)}
                                            </Text>
                                        </HStack>

                                        <Box
                                            p={4}
                                            borderRadius="lg"
                                            bg="canvas"
                                            border="1px solid"
                                            borderColor="hairline"
                                        >
                                            <VStack spacing={2} align="stretch">
                                                <Text fontSize="sm" fontWeight={500} color="bodyStrong">
                                                    {t('checkout.plan_includes')}
                                                </Text>
                                                <Text fontSize="sm" color="body">✓ {t('checkout.days_access', { days: selectedPlan.number_of_days })}</Text>
                                                <Text fontSize="sm" color="body">✓ {t('checkout.all_practice_tests')}</Text>
                                                <Text fontSize="sm" color="body">✓ {t('checkout.writing_speaking_tasks')}</Text>
                                            </VStack>
                                        </Box>
                                    </>
                                )}

                                {!selectedPlan && (
                                    <Text fontSize="sm" color="muted">
                                        {t('checkout.no_plan_selected')}
                                    </Text>
                                )}
                            </VStack>
                        </Box>
                    </Grid>
                </VStack>
            </Container>
        </Box>
    );
};

export default CheckoutForm;
